import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useLocationStore } from "../hooks/useLocationStore";
import "../assets/style/StudioSelectorDialog.css";

/**
 * StudioSelectorDialog — modal dialog shown on startup (after cookie acceptance)
 * to let the user pick which studio they want.
 *
 * Only shows when:
 *  - cookiePolicyAccepted is true
 *  - studioDialogDisabled is false
 *  - there are 2+ main locations
 *  - locations have finished loading
 */
const StudioSelectorDialog = ({ cookiePolicyAccepted }) => {
  const {
    mainLocations,
    selectedLocationId,
    setSelectedLocationId,
    studioDialogDisabled,
    setStudioDialogDisabled,
    forceDialogOpen,
    closeStudioDialog,
    loading,
  } = useLocationStore((state) => ({
    mainLocations: state.mainLocations,
    selectedLocationId: state.selectedLocationId,
    setSelectedLocationId: state.setSelectedLocationId,
    studioDialogDisabled: state.studioDialogDisabled,
    setStudioDialogDisabled: state.setStudioDialogDisabled,
    forceDialogOpen: state.forceDialogOpen,
    closeStudioDialog: state.closeStudioDialog,
    loading: state.loading,
  }));

  // Local state for the selection within the dialog (so we don't commit until confirm)
  const [localSelectedId, setLocalSelectedId] = useState(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isForced = forceDialogOpen && !loading && mainLocations.length >= 2;

  // Determine if dialog should be open
  const shouldShow =
    isForced ||
    (cookiePolicyAccepted &&
    !studioDialogDisabled &&
    !dismissed &&
    !loading &&
    mainLocations.length >= 2);

  // Initialize local selection from store when dialog becomes visible
  const effectiveLocalId = localSelectedId ?? selectedLocationId;

  const handleConfirm = () => {
    if (effectiveLocalId) {
      setSelectedLocationId(effectiveLocalId);
    }
    if (dontShowAgain) {
      setStudioDialogDisabled(true);
    }
    setDismissed(true);
    closeStudioDialog();
  };

  const handleCardClick = (id) => {
    setLocalSelectedId(id);
  };

  // Prevent closing via backdrop or escape — user must click "Wybierz"
  const handleClose = (event, reason) => {
    if (
      reason === "backdropClick" ||
      reason === "escapeKeyDown" ||
      reason === "backdropMouseDown"
    ) {
      return;
    }
  };

  return (
    <Dialog
      open={shouldShow}
      onClose={handleClose}
      className="studio-dialog"
      aria-labelledby="studio-dialog-title"
    >
      <DialogTitle id="studio-dialog-title" className="studio-dialog__title">
        Wybierz Studio
      </DialogTitle>
      <DialogContent>
        <p className="studio-dialog__subtitle">
          Wybierz lokalizację, aby zobaczyć grafik i szczegóły
        </p>
        <div className="studio-dialog__list">
          {mainLocations.map((location) => {
            const isActive = effectiveLocalId === location.id;
            return (
              <button
                key={location.id}
                className={`studio-dialog__card ${
                  isActive ? "studio-dialog__card--active" : ""
                }`}
                onClick={() => handleCardClick(location.id)}
                aria-pressed={isActive}
                type="button"
              >
                <div className="studio-dialog__card-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                  </svg>
                </div>
                <div className="studio-dialog__card-content">
                  <span className="studio-dialog__card-name">
                    {location.timetable_name || location.name}
                  </span>
                  <span className="studio-dialog__card-address">
                    {location.address}
                  </span>
                </div>
                {isActive && (
                  <div className="studio-dialog__card-check">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="studio-dialog__checkbox-row">
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                size="small"
              />
            }
            label="Nie pokazuj ponownie"
          />
        </div>
      </DialogContent>
      <DialogActions className="studio-dialog__actions">
        <Button variant="contained" onClick={handleConfirm}>
          Wybierz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudioSelectorDialog;
